import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CreditCard, Plus, X, Loader2, Pencil, Trash2, Check, Ban, QrCode, ScanLine, UserCheck, FileUp } from "lucide-react";
import aapi, { inr, fmtDate, formatApiError } from "./aapi";
import { PageHeader, EmptyState, inputCls, selectCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const TYPE_LABELS = { upi: "UPI", bank: "Bank Transfer", crypto: "Crypto", other: "Other" };
const PAY_STYLES = {
  pending: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  approved: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  rejected: "border-rose-400/30 bg-rose-400/10 text-rose-300",
};
const EMPTY_FORM = { name: "", type: "upi", details: "", instructions: "", qr_image: null, enabled: true };

export default function AdminPayments() {
  const [tab, setTab] = useState("submissions");
  const [status, setStatus] = useState("pending");
  const [payments, setPayments] = useState(null);
  const [methods, setMethods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const [docReady, setDocReady] = useState(false);
  const [docForm, setDocForm] = useState({ utr: "", amount: "", datetime: "", payer: "", note: "" });
  const [docUserSearch, setDocUserSearch] = useState("");
  const [docUsers, setDocUsers] = useState([]);
  const [docUser, setDocUser] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkPages, setBulkPages] = useState(0);
  const [bulkUserSearch, setBulkUserSearch] = useState("");
  const [bulkUsers, setBulkUsers] = useState([]);
  const [bulkUser, setBulkUser] = useState(null);
  const [bulkQuery, setBulkQuery] = useState("");
  const [bulkMeta, setBulkMeta] = useState(null);

  const loadPayments = () =>
    aapi.get("/payments", { params: { status } }).then(({ data }) => setPayments(data)).catch(() => setPayments([]));
  const loadMethods = () => aapi.get("/payment-methods").then(({ data }) => setMethods(data)).catch(() => {});

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  useEffect(() => {
    loadMethods();
  }, []);

  const review = async (p, action) => {
    const msg = action === "approve"
      ? `Approve ${inr(p.amount)} from ${p.user_email}? The wallet is credited immediately.`
      : `Reject ${inr(p.amount)} from ${p.user_email}?`;
    if (!window.confirm(msg)) return;
    try {
      await aapi.post(`/payments/${p.id}/review`, { action });
      toast.success(`Payment ${action}d — customer notified, audit logged`);
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onQr = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) return toast.error("QR image must be under 500KB");
    const r = new FileReader();
    r.onload = () => setForm((prev) => ({ ...prev, qr_image: r.result }));
    r.readAsDataURL(f);
  };

  const saveMethod = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await aapi.post("/payment-methods", form);
        toast.success("Payment method added — visible to customers now");
      } else {
        await aapi.put(`/payment-methods/${editing}`, form);
        toast.success("Payment method updated");
      }
      setEditing(null);
      loadMethods();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleMethod = async (m) => {
    await aapi.put(`/payment-methods/${m.id}`, { ...m, enabled: !m.enabled })
      .then(() => { toast.success(m.enabled ? "Method disabled" : "Method enabled"); loadMethods(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const removeMethod = async (m) => {
    if (!window.confirm(`Delete payment method "${m.name}"?`)) return;
    await aapi.delete(`/payment-methods/${m.id}`).then(() => { toast.success("Deleted"); loadMethods(); })
      .catch((e) => toast.error(formatApiError(e)));
  };

  // ----------------------------- document auto-fill -----------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      aapi.get("/users", { params: docUserSearch.trim() ? { search: docUserSearch.trim() } : {} })
        .then(({ data }) => setDocUsers(data))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [docUserSearch]);

  const onDocFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("File 8MB se choti honi chahiye");
    setDocBusy(true);
    setDocReady(false);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await aapi.post("/payments/extract-screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocForm({
        utr: data.utr || "",
        amount: data.amount ? String(data.amount) : "",
        datetime: data.date ? `${data.date}T${data.time || "12:00"}` : "",
        payer: data.payer_name || "",
        note: [data.payer_name && `Paid by ${data.payer_name}`, data.app].filter(Boolean).join(" via "),
      });
      setDocReady(true);
      toast.success("Document se details fetch ho gayi", { description: "Fields verify/edit karein, user select karein, phir confirm." });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDocBusy(false);
    }
  };

  const applyDoc = async () => {
    if (!docUser) return toast.error("Customer select karein");
    if (!docForm.amount || parseFloat(docForm.amount) <= 0) return toast.error("Valid amount daalein");
    if (!window.confirm(`${docUser.email} ko ${inr(parseFloat(docForm.amount))} credit karein?${docForm.utr ? `\nUTR: ${docForm.utr}` : ""}\nAudit record banega.`)) return;
    setDocBusy(true);
    try {
      const payload = { action: "add", amount: parseFloat(docForm.amount), reference: docForm.utr, note: docForm.note };
      if (docForm.datetime) payload.created_at = new Date(docForm.datetime).toISOString();
      await aapi.post(`/users/${docUser.id}/balance`, payload);
      toast.success(`${inr(parseFloat(docForm.amount))} credited to ${docUser.name}`, { description: "Wallet updated — audit logged, customer notified." });
      setDocReady(false);
      setDocForm({ utr: "", amount: "", datetime: "", payer: "", note: "" });
      setDocUser(null);
      loadPayments();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDocBusy(false);
    }
  };

  // ----------------------------- bulk statement import -----------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      aapi.get("/users", { params: bulkUserSearch.trim() ? { search: bulkUserSearch.trim() } : {} })
        .then(({ data }) => setBulkUsers(data))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [bulkUserSearch]);

  const onBulkFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("File 10MB se choti honi chahiye");
    setBulkBusy(true);
    setBulkRows([]);
    setBulkMeta(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (bulkQuery.trim()) fd.append("query", bulkQuery.trim());
      const { data } = await aapi.post("/payments/extract-statement", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setBulkRows(data.transactions.map((t, i) => ({ ...t, key: `r${i}`, selected: t.type === "credit" })));
      setBulkPages(data.pages);
      setBulkMeta(data);
      if (!data.transactions.length) {
        toast.info(`"${bulkQuery.trim()}" ke naam pe koi transaction nahi mili`, { description: "Spelling check karke dobara try karein." });
      } else {
        toast.success(`${data.count} transaction${data.count === 1 ? "" : "s"} mil gayi${data.query ? ` — "${data.query}"` : ""}`, {
          description: data.mode === "text" ? "Instant text-mode (kam credits, 100% accurate) — review karke credit karein." : "Vision mode (scanned doc) — review zaroor karein.",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const creditRows = bulkRows.filter((r) => r.type === "credit");
  const selectedRows = bulkRows.filter((r) => r.selected && r.type === "credit");
  const selectedTotal = selectedRows.reduce((s, r) => s + r.amount, 0);

  const applyBulk = async () => {
    if (!bulkUser) return toast.error("Customer select karein");
    if (!selectedRows.length) return toast.error("Kam se kam ek credit row select karein");
    if (!window.confirm(`${bulkUser.email} ko ${selectedRows.length} transactions ka total ${inr(selectedTotal)} credit karein?\nHar transaction ka alag audit record banega.`)) return;
    setBulkBusy(true);
    let done = 0, failed = 0;
    for (const r of selectedRows) {
      try {
        const payload = { action: "add", amount: r.amount, reference: r.utr || "", note: r.payer_name ? `Bulk import — paid by ${r.payer_name}` : "Bulk statement import" };
        if (r.date) payload.created_at = new Date(`${r.date}T${r.time || "12:00"}`).toISOString();
        await aapi.post(`/users/${bulkUser.id}/balance`, payload);
        done++;
        setBulkRows((prev) => prev.filter((x) => x.key !== r.key));
      } catch {
        failed++;
      }
    }
    setBulkBusy(false);
    if (done) toast.success(`${done} transaction${done > 1 ? "s" : ""} credited — ${inr(selectedTotal)}`, { description: failed ? `${failed} failed — rows table mein hain, retry karein.` : "Wallet updated, customer notified." });
    if (failed) toast.error(`${failed} transaction credit nahi ho payi`);
    loadPayments();
  };

  return (
    <div data-testid="admin-payments-page">
      <PageHeader title="Payment Management" sub="Methods, UPI QRs and every rupee awaiting verification.">
        <div className="flex gap-2">
          {["submissions", "document", "methods"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`payments-tab-${t}`}
              className={`rounded-full border px-5 py-2.5 text-xs font-semibold capitalize transition-all duration-300 ${
                tab === t ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40"
              }`}>
              {t === "submissions" ? "Payment Submissions" : t === "document" ? "Document Auto-Fill" : "Payment Methods"}
            </button>
          ))}
        </div>
      </PageHeader>

      {tab === "submissions" && (
        <>
          <div className="mb-6 flex flex-wrap gap-2">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`payments-status-${s}`}
                className={`rounded-full border px-4 py-2 text-xs font-medium capitalize transition-all duration-300 ${
                  status === s ? "border-gold bg-gold/10 text-goldlight" : "border-white/10 text-neutral-400 hover:border-gold/40"
                }`}>
                {s === "approved" ? "Successful" : s === "rejected" ? "Failed" : s}
              </button>
            ))}
          </div>

          {payments === null ? (
            <div className="flex justify-center py-24">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
            </div>
          ) : payments.length === 0 ? (
            <EmptyState icon={CreditCard} title="No payments here" sub="Customer UPI/bank submissions will appear for review." testid="payments-empty" />
          ) : (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
              className="space-y-3" data-testid="payments-list">
              {payments.map((p) => (
                <div key={p.id} data-testid={`payment-row-${p.id}`}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-panel/80 px-6 py-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-semibold text-neutral-100">{inr(p.amount)}</span>
                      <span className="rounded-full border border-white/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-neutral-400">
                        {p.method_name}
                      </span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${PAY_STYLES[p.status]}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="mt-1.5 font-mono text-[11px] text-neutral-500">
                      {p.user_email} · REF {p.reference} · {fmtDate(p.created_at)}
                    </div>
                    {p.signature && (
                      <div className="mt-1 font-mono text-[10px] text-emerald-400/80">
                        SIGNED: {p.signature} · TERMS ACCEPTED
                      </div>
                    )}
                    {p.note && <div className="mt-1 text-xs text-neutral-500">Note: {p.note}</div>}
                  </div>
                  {p.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => review(p, "approve")} data-testid={`payment-approve-${p.id}`}
                        className="flex items-center gap-1.5 rounded-full bg-gradient-to-br from-goldlight to-golddark px-4 py-2 text-xs font-bold text-black">
                        <Check size={13} /> Approve & Credit
                      </button>
                      <button onClick={() => review(p, "reject")} data-testid={`payment-reject-${p.id}`}
                        className="flex items-center gap-1.5 rounded-full border border-rose-400/40 px-4 py-2 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-400/10">
                        <Ban size={13} /> Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </>
      )}

      {tab === "document" && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="grid gap-6 lg:grid-cols-2" data-testid="doc-autofill-panel">
          {/* upload + extracted fields */}
          <div className="rounded-3xl border border-gold/20 bg-panel p-7">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                <ScanLine size={18} className="text-gold" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">PAYMENT DOCUMENT → AUTO-FILL</div>
                <p className="mt-1 text-xs text-neutral-500">Screenshot ya PDF upload karein — UTR, amount, date & time AI fetch karega.</p>
              </div>
            </div>

            <label
              className="mt-6 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-gold/[0.04] px-6 py-10 text-center transition-colors hover:border-gold/70"
              data-testid="doc-upload-zone">
              {docBusy ? <Loader2 size={22} className="animate-spin text-gold" /> : <FileUp size={22} className="text-gold" />}
              <span className="text-sm font-medium text-goldlight">
                {docBusy ? "Document read ho raha hai…" : "Payment screenshot / PDF upload karein"}
              </span>
              <span className="font-mono text-[9px] tracking-[0.12em] text-neutral-600">JPG · PNG · WEBP · PDF — MAX 8MB</span>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={onDocFile} data-testid="doc-file-input" />
            </label>

            {docReady && (
              <div className="mt-5 space-y-3" data-testid="doc-extracted-fields">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">UTR / Transaction ID</label>
                    <input value={docForm.utr} onChange={(e) => setDocForm({ ...docForm, utr: e.target.value })} className={`${inputCls} mt-1.5 font-mono text-xs`} data-testid="doc-utr-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">Amount ₹</label>
                    <input type="number" min="0" value={docForm.amount} onChange={(e) => setDocForm({ ...docForm, amount: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="doc-amount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">Date & time</label>
                    <input type="datetime-local" value={docForm.datetime} onChange={(e) => setDocForm({ ...docForm, datetime: e.target.value })} className={`${inputCls} mt-1.5 text-xs`} data-testid="doc-datetime-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">Paid by</label>
                    <input value={docForm.payer} onChange={(e) => setDocForm({ ...docForm, payer: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="doc-payer-input" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">Note</label>
                  <input value={docForm.note} onChange={(e) => setDocForm({ ...docForm, note: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="doc-note-input" />
                </div>
                <p className="font-mono text-[9px] leading-relaxed tracking-[0.08em] text-neutral-600">
                  FETCHED BY AI — har field editable hai. Galat value ho to theek karke confirm karein.
                </p>
              </div>
            )}
          </div>

          {/* user picker + confirm */}
          <div className="rounded-3xl border border-white/10 bg-panel p-7" data-testid="doc-user-card">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                <UserCheck size={18} className="text-gold" />
              </div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">CREDIT TO CUSTOMER</div>
                <p className="mt-1 text-xs text-neutral-500">User manually select karein — wallet mein amount jud jayega.</p>
              </div>
            </div>

            {docUser ? (
              <div className="mt-5 flex items-center justify-between rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-3" data-testid="doc-selected-user">
                <div>
                  <p className="text-sm font-semibold text-neutral-100">{docUser.name}</p>
                  <p className="font-mono text-[10px] text-neutral-500">{docUser.email}</p>
                </div>
                <button onClick={() => setDocUser(null)} data-testid="doc-change-user"
                  className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-neutral-500 hover:text-goldlight">
                  <X size={12} /> CHANGE
                </button>
              </div>
            ) : (
              <>
                <input value={docUserSearch} onChange={(e) => setDocUserSearch(e.target.value)}
                  placeholder="Name ya email se customer dhundhein…" className={`${inputCls} mt-5`} data-testid="doc-user-search" />
                <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-1" data-testid="doc-user-list">
                  {docUsers.slice(0, 25).map((u) => (
                    <button key={u.id} onClick={() => { setDocUser(u); setDocUserSearch(""); }} data-testid={`doc-user-${u.id}`}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs text-neutral-300 transition-colors hover:bg-gold/[0.07] hover:text-goldlight">
                      <span className="truncate">{u.name}</span>
                      <span className="ml-auto truncate font-mono text-[10px] text-neutral-600">{u.email}</span>
                    </button>
                  ))}
                  {!docUsers.length && <p className="px-3 py-4 text-center text-xs text-neutral-600">Koi user nahi mila</p>}
                </div>
              </>
            )}

            <button onClick={applyDoc} disabled={docBusy || !docReady || !docUser} data-testid="doc-apply-button"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
              {docBusy && <Loader2 size={15} className="animate-spin" />}
              CREDIT {docForm.amount ? inr(parseFloat(docForm.amount) || 0) : "AMOUNT"} — AUDIT LOGGED
            </button>
            {!docReady && (
              <p className="mt-3 text-center font-mono text-[9px] tracking-[0.12em] text-neutral-600">
                PEHLE DOCUMENT UPLOAD KAREIN — PHIR USER SELECT KARKE CREDIT CONFIRM HOGAA
              </p>
            )}
          </div>

          {/* ------- bulk statement import ------- */}
          <div className="rounded-3xl border border-white/10 bg-panel p-7 lg:col-span-2" data-testid="bulk-import-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                  <FileUp size={18} className="text-gold" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">BULK STATEMENT IMPORT</div>
                  <p className="mt-1 text-xs text-neutral-500">Pura bank/UPI statement PDF upload karein — saare transactions payer name ke saath review table mein aayenge.</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={bulkQuery} onChange={(e) => setBulkQuery(e.target.value)}
                  placeholder='Sirf ek vyakti ka? naam likhein — e.g. "Rahul Sharma"'
                  className="w-64 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 text-xs text-neutral-100 outline-none transition-colors placeholder:text-neutral-600 focus:border-gold/60"
                  data-testid="bulk-query-input" />
                <label
                  className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-gold/40 bg-gold/[0.05] px-4 py-3 text-xs font-medium text-goldlight transition-colors hover:border-gold/70"
                  data-testid="bulk-upload-button">
                  {bulkBusy && !bulkRows.length ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
                  {bulkBusy && !bulkRows.length ? "Statement padha ja raha hai…" : bulkRows.length ? "Doosra statement upload karein" : "Statement PDF upload karein"}
                  <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={onBulkFile} data-testid="bulk-file-input" />
                </label>
              </div>
            </div>

            {bulkRows.length > 0 && (
              <>
                {bulkMeta && (
                  <p className="mt-4 flex flex-wrap items-center gap-2 font-mono text-[9px] tracking-[0.12em] text-neutral-500" data-testid="bulk-meta">
                    {bulkMeta.query ? <span className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-1 text-goldlight">FILTER: "{bulkMeta.query.toUpperCase()}"</span> : null}
                    <span>{bulkMeta.count} ROWS · CREDITS {inr(bulkMeta.credit_total)}</span>
                    <span className={`rounded-full border px-2.5 py-1 ${bulkMeta.mode === "text" ? "border-emerald-400/30 text-emerald-300" : "border-amber-400/30 text-amber-300"}`}>
                      {bulkMeta.mode === "text" ? "INSTANT TEXT MODE — LOW CREDIT" : "VISION MODE — SCANNED DOC"}
                    </span>
                  </p>
                )}
                <div className="mt-3 overflow-x-auto rounded-2xl border border-white/10" data-testid="bulk-review-table">
                  <div className="grid min-w-[720px] grid-cols-12 gap-2 border-b border-white/10 bg-white/[0.02] px-4 py-3 font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                    <span className="col-span-1 flex items-center">
                      <input type="checkbox"
                        checked={creditRows.length > 0 && selectedRows.length === creditRows.length}
                        onChange={(e) => setBulkRows((prev) => prev.map((r) => ({ ...r, selected: r.type === "credit" ? e.target.checked : false })))}
                        className="h-3.5 w-3.5 accent-[rgb(var(--gold))]" data-testid="bulk-select-all" aria-label="Select all credits" />
                    </span>
                    <span className="col-span-2">DATE & TIME</span>
                    <span className="col-span-3">PAYER NAME</span>
                    <span className="col-span-2">UTR / REF</span>
                    <span className="col-span-2 text-right">AMOUNT</span>
                    <span className="col-span-2 text-right">TYPE</span>
                  </div>
                  {bulkRows.map((r) => (
                    <div key={r.key}
                      className={`grid min-w-[720px] grid-cols-12 items-center gap-2 border-b border-white/5 px-4 py-2.5 text-xs transition-colors last:border-0 ${
                        r.type === "debit" ? "opacity-45" : r.selected ? "bg-gold/[0.05]" : "hover:bg-white/[0.02]"
                      }`}
                      data-testid={`bulk-row-${r.key}`}>
                      <span className="col-span-1">
                        {r.type === "credit" ? (
                          <input type="checkbox" checked={!!r.selected}
                            onChange={(e) => setBulkRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, selected: e.target.checked } : x)))}
                            className="h-3.5 w-3.5 accent-[rgb(var(--gold))]" data-testid={`bulk-select-${r.key}`} aria-label="Select row" />
                        ) : null}
                      </span>
                      <span className="col-span-2 font-mono text-[10px] text-neutral-400">
                        {r.date || "—"}{r.time ? ` ${r.time}` : ""}
                      </span>
                      <span className="col-span-3 truncate font-medium text-neutral-100" data-testid={`bulk-payer-${r.key}`}>
                        {r.payer_name || "—"}
                      </span>
                      <span className="col-span-2 truncate font-mono text-[10px] text-neutral-500">{r.utr || "—"}</span>
                      <span className={`col-span-2 text-right font-semibold ${r.type === "credit" ? "text-emerald-300" : "text-rose-300"}`}>
                        {inr(r.amount)}
                      </span>
                      <span className="col-span-2 flex items-center justify-end gap-2">
                        <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] ${
                          r.type === "credit" ? "border-emerald-400/30 text-emerald-300" : "border-rose-400/30 text-rose-300"
                        }`}>{r.type}</span>
                        <button onClick={() => setBulkRows((prev) => prev.filter((x) => x.key !== r.key))}
                          aria-label="Remove row" data-testid={`bulk-remove-${r.key}`}
                          className="text-neutral-600 transition-colors hover:text-rose-300">
                          <X size={13} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                    {bulkUser ? (
                      <div className="flex items-center justify-between" data-testid="bulk-selected-user">
                        <div>
                          <p className="text-sm font-semibold text-neutral-100">{bulkUser.name}</p>
                          <p className="font-mono text-[10px] text-neutral-500">{bulkUser.email}</p>
                        </div>
                        <button onClick={() => setBulkUser(null)} data-testid="bulk-change-user"
                          className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-neutral-500 hover:text-goldlight">
                          <X size={12} /> CHANGE
                        </button>
                      </div>
                    ) : (
                      <>
                        <input value={bulkUserSearch} onChange={(e) => setBulkUserSearch(e.target.value)}
                          placeholder="Credit kis customer ko? name/email search…" className={inputCls} data-testid="bulk-user-search" />
                        <div className="mt-2 max-h-32 overflow-y-auto rounded-xl border border-white/10 p-1" data-testid="bulk-user-list">
                          {bulkUsers.slice(0, 15).map((u) => (
                            <button key={u.id} onClick={() => { setBulkUser(u); setBulkUserSearch(""); }} data-testid={`bulk-user-${u.id}`}
                              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs text-neutral-300 transition-colors hover:bg-gold/[0.07] hover:text-goldlight">
                              <span className="truncate">{u.name}</span>
                              <span className="ml-auto truncate font-mono text-[10px] text-neutral-600">{u.email}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col justify-center rounded-2xl border border-gold/20 bg-gold/[0.04] p-4">
                    <p className="font-mono text-[10px] tracking-[0.15em] text-neutral-400" data-testid="bulk-summary">
                      {selectedRows.length} CREDIT ROW{selectedRows.length === 1 ? "" : "S"} SELECTED · TOTAL {inr(selectedTotal)}
                    </p>
                    <button onClick={applyBulk} disabled={bulkBusy || !selectedRows.length || !bulkUser} data-testid="bulk-apply-button"
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3 text-xs font-bold tracking-[0.12em] text-black disabled:opacity-40">
                      {bulkBusy && <Loader2 size={14} className="animate-spin" />}
                      CREDIT SELECTED TO {bulkUser ? bulkUser.name.toUpperCase() : "CUSTOMER"}
                    </button>
                  </div>
                </div>
              </>
            )}
            {!bulkRows.length && !bulkBusy && (
              <p className="mt-5 text-center font-mono text-[9px] tracking-[0.12em] text-neutral-600" data-testid="bulk-empty-hint">
                STATEMENT UPLOAD KARTE HI SAARE TRANSACTIONS (DATE · PAYER NAME · UTR · AMOUNT) YAHAN REVIEW TABLE MEIN AAYENGE
              </p>
            )}
          </div>
        </motion.div>
      )}

      {tab === "methods" && (
        <>
          <div className="mb-6">
            <button onClick={() => { setForm(EMPTY_FORM); setEditing("new"); }} data-testid="add-method-button"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-br from-goldlight via-gold to-golddark px-5 py-2.5 text-xs font-bold text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)]">
              <Plus size={14} /> Add Payment Method
            </button>
          </div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="grid gap-4 sm:grid-cols-2" data-testid="methods-list">
            {methods.length === 0 && (
              <EmptyState icon={QrCode} title="No payment methods" sub="Add a UPI ID or bank account with a QR to start collecting." testid="methods-empty" />
            )}
            {methods.map((m) => (
              <div key={m.id} data-testid={`method-card-${m.name}`}
                className={`rounded-2xl border p-6 transition-colors duration-300 ${m.enabled ? "border-white/10 bg-panel/80" : "border-white/5 bg-panel/40 opacity-60"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {m.qr_image ? (
                      <img src={m.qr_image} alt={`${m.name} QR`} className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
                    ) : (
                      <span className="flex h-14 w-14 items-center justify-center rounded-xl border border-gold/25 bg-gold/[0.06] text-gold">
                        <QrCode size={22} />
                      </span>
                    )}
                    <div>
                      <div className="text-sm font-semibold text-neutral-100">{m.name}</div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="rounded-full border border-gold/30 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-goldlight">
                          {TYPE_LABELS[m.type]}
                        </span>
                        <span className={`font-mono text-[9px] uppercase tracking-wider ${m.enabled ? "text-emerald-400" : "text-neutral-600"}`}>
                          {m.enabled ? "Live" : "Off"}
                        </span>
                      </div>
                      {m.details && <div className="mt-1.5 font-mono text-[11px] text-neutral-400">{m.details}</div>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setForm({ name: m.name, type: m.type, details: m.details, instructions: m.instructions, qr_image: m.qr_image, enabled: m.enabled }); setEditing(m.id); }}
                      aria-label="Edit method" data-testid={`method-edit-${m.name}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-gold/30 text-goldlight transition-colors hover:bg-gold hover:text-black">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => removeMethod(m)} aria-label="Delete method" data-testid={`method-delete-${m.name}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-400/30 text-rose-300 transition-colors hover:bg-rose-400/10">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-neutral-500">{m.instructions || "—"}</span>
                  <button onClick={() => toggleMethod(m)} aria-label="Toggle method" data-testid={`method-toggle-${m.name}`}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-300 ${m.enabled ? "bg-gold" : "bg-white/10"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all duration-300 ${m.enabled ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        </>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setEditing(null)}>
            <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }} transition={{ duration: 0.5, ease: EASE }}
              className="w-full max-w-lg rounded-3xl border border-gold/20 bg-panel p-8"
              onClick={(e) => e.stopPropagation()} data-testid="method-modal">
              <div className="flex items-start justify-between">
                <h3 className="text-xl font-bold text-neutral-50">{editing === "new" ? "Add Payment Method" : "Edit Payment Method"}</h3>
                <button onClick={() => setEditing(null)} aria-label="Close" data-testid="method-modal-close"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-neutral-400 hover:border-gold/50 hover:text-neutral-100">
                  <X size={16} />
                </button>
              </div>
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input placeholder="Name (e.g. UPI — Primary)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls} data-testid="method-name-input" />
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={selectCls} data-testid="method-type-select">
                    {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <input placeholder={form.type === "upi" ? "UPI ID (e.g. beatkingsmm@upi)" : "Account details / wallet address"}
                  value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
                  className={inputCls} data-testid="method-details-input" />
                <input placeholder="Instructions (e.g. Send exact amount, then paste UTR below)"
                  value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  className={inputCls} data-testid="method-instructions-input" />
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">UPI QR image (optional, max 500KB)</label>
                  <div className="mt-2 flex items-center gap-4">
                    <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-gold/40 px-4 py-3 text-xs font-medium text-goldlight transition-colors hover:border-gold" data-testid="method-qr-upload">
                      <QrCode size={15} /> {form.qr_image ? "Replace QR" : "Upload QR"}
                      <input type="file" accept="image/*" className="hidden" onChange={onQr} data-testid="method-qr-file" />
                    </label>
                    {form.qr_image && (
                      <img src={form.qr_image} alt="QR preview" className="h-16 w-16 rounded-xl border border-white/10 object-cover" data-testid="method-qr-preview" />
                    )}
                  </div>
                </div>
                <button onClick={saveMethod} disabled={busy || form.name.trim().length < 2} data-testid="method-save-button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-50">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  {editing === "new" ? "ADD METHOD" : "SAVE CHANGES"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
